<script setup lang="ts">
  import { Floating, Typo, TypoVariant } from '@travelata-libs/ui-kit';
  import { useCloseOnEscape } from '@/entities/searchForm';
  import NightsStepper from './NightsStepper.vue';
  import { NIGHTS_RANGE_TEXTS } from '../config/texts';

  interface NightsRangeDropdownProps {
    from: number;
    to: number;
    visible: boolean;
    summary: string;
  }

  const props = defineProps<NightsRangeDropdownProps>();

  interface NightsRangeDropdownEmits {
    (e: 'update:from', v: number): void;
    (e: 'update:to', v: number): void;
    (e: 'close'): void;
  }

  const emit = defineEmits<NightsRangeDropdownEmits>();

  const onFloatingHide = (): void => {
    if (props.visible) {
      emit('close');
    }
  };

  useCloseOnEscape(
    () => props.visible,
    () => emit('close'),
  );
</script>

<template>
  <Floating
    class="nights-range-dropdown__floating"
    :visible="visible"
    :disable-flip="true"
    :teleported="false"
    placement="bottom-start"
    @hide="onFloatingHide"
  >
    <template #reference>
      <slot name="reference" />
    </template>

    <template #floating>
      <div
        class="nights-range-dropdown"
        data-testid="nights-range-dropdown"
      >
        <div class="nights-range-dropdown__steppers">
          <div class="nights-range-dropdown__stepper">
            <Typo variant="body-l-400">От</Typo>
            <NightsStepper
              :label="NIGHTS_RANGE_TEXTS.fromLabel"
              :value="from"
              testid="nights-range-from"
              @update:value="emit('update:from', $event)"
            />
          </div>
          <div class="nights-range-dropdown__stepper">
            <Typo variant="body-l-400">До</Typo>
            <NightsStepper
              :label="NIGHTS_RANGE_TEXTS.toLabel"
              :value="to"
              testid="nights-range-to"
              @update:value="emit('update:to', $event)"
            />
          </div>
        </div>

        <div
          class="nights-range-dropdown__summary"
          data-testid="nights-range-summary"
        >
          <Typo
            :variant="TypoVariant.BodyLRegular"
            tag="span"
          >
            {{ summary }}
          </Typo>
        </div>
      </div>
    </template>
  </Floating>
</template>

<style scoped lang="scss">
  .nights-range-dropdown {
    background-color: var(--color-bg-primary);
    border-radius: var(--rounding-s);
    box-shadow: var(--shadow-s);
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    gap: var(--spacing-xl);
    min-width: 264px;
    padding: var(--spacing-l) var(--spacing-m);
    width: max-content;

    &__stepper {
      align-items: center;
      display: flex;
      gap: var(--spacing-s);
    }

    &__steppers {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-l);
    }

    &__summary {
      color: var(--color-text-primary);
    }
  }
</style>
